import type { FC, FormEvent } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import type { IDealPaymentV2Content } from '@eneco/cms-types/generated/IDealPaymentV2';
import { useApplication } from '@eneco/common/application';
import { getStaticPrefix } from '@eneco/common/env';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { ComponentError } from '@eneco/components/ui/ComponentError/ComponentError';
import {
  useFinancialsPublicV3CreatePaymentTransactionForInvoiceFromEncryptedQueryStringV3,
  useFinancialsPublicV3CreatePaymentTransactionForPaymentArrangementFromEncryptedQueryStringV3,
  useFinancialsPublicV3GetPaymentTransactionStatusFromEncryptedQueryStringV3,
} from '@eneco/dc/hooks';
import { keepCmsOverride } from '@eneco/next';
import {
  Bucket,
  Button,
  ButtonLink,
  Divider,
  Form,
  Heading,
  Image,
  NotificationBox,
  Skeleton,
  Stack,
} from '@eneco/sparky';
import type { NotificationBoxProps } from '@eneco/sparky/types';

const IDEAL_LOGO_SIZE = '32px';

type TransactionStatus = 'Success' | 'Cancelled' | 'Expired' | 'Open' | 'Error';

type NotificationField = 'notificationSuccessField' | 'notificationErrorField' | 'notificationAlreadyPaidField';

const STATUS_NOTIFICATION: Record<TransactionStatus, NotificationField> = {
  Success: 'notificationSuccessField',
  Cancelled: 'notificationErrorField',
  Expired: 'notificationErrorField',
  Open: 'notificationAlreadyPaidField',
  Error: 'notificationErrorField',
};

const iDealLogoUrl = () => `${getStaticPrefix()}/images/multilabel/ideal-wero.png`;

const IDealPaymentForm: FC = () => {
  const { adapter } = useCms();
  const { cardTitle, generalTitle, payButtonLabel, notificationErrorField } = useContent<IDealPaymentV2Content>();
  const { searchParams } = useApplication();

  const invoiceType = (searchParams.get('type') || 'nota') as 'nota' | 'betaaltermijn';

  const usePaymentTransactionForInvoice =
    useFinancialsPublicV3CreatePaymentTransactionForInvoiceFromEncryptedQueryStringV3();
  const usePaymentTransactionForPaymentArrangement =
    useFinancialsPublicV3CreatePaymentTransactionForPaymentArrangementFromEncryptedQueryStringV3();

  const {
    send,
    isLoading,
    isError: isCreatePaymentLinkError,
  } = invoiceType === 'nota' ? usePaymentTransactionForInvoice : usePaymentTransactionForPaymentArrangement;

  const query = searchParams.get('query') || searchParams.get('id');
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const response = await send({
      query,
      // Keeps a `?_cms=` override, so the bank returns the visitor to the lane they paid from.
      merchantReturnUrl: keepCmsOverride(
        `${window.location.href.split('?')[0]}?query=${encodeURIComponent(query ?? '')}`,
      ),
    });

    if (response?.redirectUrl) {
      window.location.assign(response.redirectUrl);
    }
  };

  return (
    <Form onSubmit={event => handleSubmit(event)}>
      <Bucket title={cardTitle ?? ''}>
        <Bucket.Content>
          <Stack gap="6">
            <Stack gap="2" direction="row" alignY="center">
              <Image src={iDealLogoUrl()} alt="" width={IDEAL_LOGO_SIZE} />
              <Heading as="h1" size="XS">
                {generalTitle}
              </Heading>
            </Stack>
          </Stack>
          <Bucket.Actions>
            <Button isLoading={isLoading} type="submit">
              {payButtonLabel}
            </Button>
          </Bucket.Actions>
          {isCreatePaymentLinkError && (
            <Stack.Item>
              <TrackedNotificationBox
                variant="error"
                label={notificationErrorField?.title ?? ''}
                text={<RichText html={adapter.toRichText(notificationErrorField?.content)} />}
                isAlert={true}
              />
            </Stack.Item>
          )}
        </Bucket.Content>
      </Bucket>
    </Form>
  );
};

const IDealTransactionResult: FC<{ transactionId: string; query: string }> = ({ transactionId, query }) => {
  const { adapter } = useCms();
  const content = useContent<IDealPaymentV2Content>();

  const { data, error } = useFinancialsPublicV3GetPaymentTransactionStatusFromEncryptedQueryStringV3({
    query,
    paymentId: transactionId,
  });

  if (error) {
    return <ComponentError />;
  }
  if (!data) {
    return <Skeleton height={200} />;
  }
  const { status } = data as { status: TransactionStatus };
  const isTransactionSuccessful = status === 'Success';
  const isTransactionAlreadyPaid = status === 'Open';
  const isTransactionUnsuccessful = !isTransactionAlreadyPaid && !isTransactionSuccessful;

  const shouldShowViewInvoices = isTransactionSuccessful || isTransactionAlreadyPaid;

  const notification = content[STATUS_NOTIFICATION[status]];
  const viewInvoicesLink = linkWithUrl(content.viewInvoicesLink);

  return (
    <Bucket title={content.cardTitle ?? ''}>
      <Bucket.Content>
        <Stack gap="6">
          <Stack gap="2" direction="row" alignY="center">
            <Image src={iDealLogoUrl()} alt={'iDeal logo'} width={IDEAL_LOGO_SIZE} />
            {!isTransactionAlreadyPaid && (
              <Heading as="h1" size="XS">
                {isTransactionSuccessful ? content.successTitle : content.generalTitle}
              </Heading>
            )}
          </Stack>
          <NotificationBox
            isAlert={true}
            variant={notification?.variant as NotificationBoxProps['variant']}
            title={notification?.title}
            text={<RichText html={adapter.toRichText(notification?.content)} />}
          />
          {shouldShowViewInvoices && (
            <>
              <Divider />
              <Stack gap="2">
                <Heading as="h2" size="XS">
                  {content.viewInvoicesTitle}
                </Heading>
                <RichText html={adapter.toRichText(content.viewInvoicesDescription)} />
              </Stack>
            </>
          )}
        </Stack>
        {!isTransactionUnsuccessful && (
          <Bucket.Actions>
            {viewInvoicesLink && (
              <ButtonLink href={viewInvoicesLink.href} target={viewInvoicesLink.target}>
                {viewInvoicesLink.text}
              </ButtonLink>
            )}
          </Bucket.Actions>
        )}
      </Bucket.Content>
    </Bucket>
  );
};

/**
 * `IDealPaymentV2` block, serving **both** providers and both lanes: the containers' Sitecore registry
 * mounts it too, through `withContentFromProps`. It replaced the app `apps/payment/shared/multilabel`'s
 * `IDealPayment`, whose behavior it keeps.
 *
 * Without `scope`, `query` or `id` in the URL it renders the component error; with a transaction in
 * `scope` and a `query` it shows that transaction's outcome; otherwise the pay form, which starts an
 * invoice or payment-arrangement transaction (`type`) and redirects to the bank.
 *
 * Fields are read by their own names, which both adapters provide (Sanity's from its `notifications`
 * and `richText` family arrays via `libs/cms-sanity/src/unsweep.ts`). The legacy `pickBankFormField`
 * is not rendered by the legacy component either, so it is not read here.
 *
 * The "view invoices" button mirrors the legacy `useLinkComponent()`-wrapped `ButtonLink` with a plain
 * Sparky `ButtonLink` on the converted `Link`, the same substitution every other block in this lane makes,
 * and like them is left out when the link has no address.
 */
const IDealPaymentV2: FC = () => {
  const { searchParams } = useApplication();
  const encryptedPaymentId = searchParams.get('scope');
  const transactionId = encryptedPaymentId ? atob(encryptedPaymentId)?.split(':')[1] : null;
  const query = searchParams.get('query');
  if (!transactionId && !query && !searchParams.get('id')) {
    return <ComponentError />;
  }

  return transactionId && query ? (
    <IDealTransactionResult transactionId={transactionId} query={query} />
  ) : (
    <IDealPaymentForm />
  );
};

export default IDealPaymentV2;
